import React, { useEffect, useState } from "react";
import axios from "../axios";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
    format,
    startOfMonth,
    endOfDay,
    endOfMonth,
    setMonth,
    eachDayOfInterval,
} from "date-fns";
import { sl } from "date-fns/locale";
import DatePicker from "react-datepicker";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { useWidth } from "../hooks/useWidth";
import { formatEUR } from "../utils/utils";

axios.defaults.withCredentials = true;

/**
 * Utility function to format numeric values to one decimal place.
 */
function formatNumber(value) {
    if (typeof value === "number") {
        return value.toFixed(1);
    }
    if (!isNaN(parseFloat(value))) {
        return parseFloat(value).toFixed(1);
    }
    return value; // If not numeric, return as is
}

export default function CompareAgents() {
    const { isLower } = useWidth(640);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [agentsData, setAgentsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedTimeFrame, setSelectedTimeFrame] = useState("po_mesecih");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const timeFilters = [
        // Months
        {
            label: "Po mesecih",
            value: "po_mesecih",
            getDate: () => {
                const now = new Date();
                const year = now.getFullYear();
                const start = startOfMonth(new Date(year, 0)); // January 1st
                const end = endOfMonth(new Date(year, 11)); // December 31st
                return [start, end];
            },
        },
        {
            label: "Januar",
            value: "jan",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 0));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Februar",
            value: "feb",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 1));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Marec",
            value: "mar",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 2));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "April",
            value: "apr",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 3));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Maj",
            value: "maj",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 4));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Junij",
            value: "jun",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 5));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Julij",
            value: "jul",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 6));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Avgust",
            value: "avg",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 7));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "September",
            value: "sep",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 8));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Oktober",
            value: "okt",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 9));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "November",
            value: "nov",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 10));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "December",
            value: "dec",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 11));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
    ];

    const getSelectedTimeFrameLabel = () => {
        const filter = timeFilters.find((t) => t.value === selectedTimeFrame);
        if (filter.value === "custom" && startDate && endDate) {
            return `${format(startDate, "dd.MM.yyyy")} - ${format(
                endDate,
                "dd.MM.yyyy"
            )}`;
        }
        return filter ? filter.label.toUpperCase() : "NEZNANO OBDOBJE";
    };

    const handleTimeFrameSelect = (filter) => {
        setSelectedTimeFrame(filter.value);
        if (filter.value === "custom") {
            setShowDatePicker(true);
        } else {
            const [start, end] = filter.getDate();
            setDateRange([start, end]);
            setShowDatePicker(false);
        }
    };

    useEffect(() => {
        async function fetchAgentsData(year) {
            setLoading(true);
            try {
                const response = await axios.post(
                    `/api/admin/metrics/agents/comparison/${year}`,
                    {
                        month: selectedTimeFrame,
                    },
                    {
                        withCredentials: true,
                    }
                );

                setAgentsData(response.data);
                setError(null);
            } catch (error) {
                console.error("Error fetching agents data:", error);
                setError(
                    "Napaka pri nalaganju podatkov. Prosim, poskusite znova kasneje."
                );
            } finally {
                setLoading(false);
            }
        }

        fetchAgentsData(selectedYear);
    }, [selectedYear, selectedTimeFrame]);

    return (
        <>
            {/* Header */}
            <div className="mb-8 text-center">
                <div className="mb-8 text-center">
                    <h1 className="mb-2 text-xl font-bold sm:text-2xl lg:text-3xl">
                        Primerjave agentov
                    </h1>
                </div>
                <div className="flex justify-center gap-2">
                    <select
                        value={selectedYear}
                        onChange={(e) =>
                            setSelectedYear(Number(e.target.value))
                        }
                        className="px-4 py-2 bg-white border rounded-lg"
                    >
                        <option value={2024}>2024</option>
                        <option value={2025}>2025</option>
                        <option value={2026}>2026</option>
                    </select>
                </div>
            </div>

            <div className="p-4 mb-6 bg-white rounded-lg shadow-md">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                            Izberi časovno obdobje:
                        </label>
                        <Menu as="div" className="relative">
                            <MenuButton className="flex items-center justify-between w-full px-3 py-2 text-sm text-left bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <span>{getSelectedTimeFrameLabel()}</span>
                                <svg
                                    className="w-5 h-5 text-gray-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </MenuButton>
                            <MenuItems className="absolute z-10 w-full mt-1 overflow-y-auto scrolling-touch bg-white border rounded-lg shadow-lg focus:outline-none max-h-60 sm:max-h-none">
                                <div className="py-1">
                                    {timeFilters.map((filter) => (
                                        <MenuItem key={filter.value}>
                                            {({ active }) => (
                                                <button
                                                    className={`${
                                                        active
                                                            ? "bg-blue-50 text-blue-700"
                                                            : "text-gray-700"
                                                    } ${
                                                        selectedTimeFrame ===
                                                        filter.value
                                                            ? "bg-blue-50 font-medium"
                                                            : ""
                                                    } w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors duration-150`}
                                                    onClick={() =>
                                                        handleTimeFrameSelect(
                                                            filter
                                                        )
                                                    }
                                                >
                                                    {filter.label}
                                                </button>
                                            )}
                                        </MenuItem>
                                    ))}
                                </div>
                            </MenuItems>
                        </Menu>
                        {showDatePicker && (
                            <div className="absolute z-20 p-4 mt-1 -translate-x-1/2 bg-white border rounded-lg shadow-lg left-1/2">
                                <DatePicker
                                    selectsRange={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(update) => {
                                        setDateRange(update);
                                        if (update[0] && update[1]) {
                                            setShowDatePicker(false);
                                        }
                                    }}
                                    isClearable={true}
                                    inline
                                    className="react-datepicker"
                                    calendarClassName="custom-calendar"
                                    dayClassName={(date) =>
                                        date >= startDate && date <= endDate
                                            ? "bg-blue-50 text-blue-700"
                                            : undefined
                                    }
                                />
                                <div className="flex justify-end mt-4 space-x-2">
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                    >
                                        Prekliči
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (startDate && endDate) {
                                                setShowDatePicker(false);
                                            }
                                        }}
                                        className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                        disabled={!startDate || !endDate}
                                    >
                                        Potrdi
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-b-2 border-gray-900 rounded-full animate-spin"></div>
                </div>
            )}

            {error && (
                <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
                    {error}
                </div>
            )}

            {!loading && !error && !agentsData && (
                <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
                    Ni podatkov za izbrano leto in časovno obdobje.
                </div>
            )}
            {!loading && !error && agentsData && agentsData.length > 0 && (
                <div className="space-y-8">
                    <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                        <div className="px-6 py-4 border-b border-blue-100 bg-blue-50">
                            <h2 className="text-xl font-bold text-blue-900">
                                AGENTI
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="mb-6 overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="sticky left-0 px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase bg-gray-50"
                                            >
                                                Ime
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                {selectedTimeFrame !==
                                                "po_mesecih"
                                                    ? getSelectedTimeFrameLabel()
                                                    : "Lanski promet"}
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                Letošnji promet
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                sprememba v procentih
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                                            >
                                                število izdanih računov
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {agentsData.map((row, index) => (
                                            <tr
                                                key={index}
                                                className={
                                                    index % 2 === 0
                                                        ? "bg-white"
                                                        : "bg-gray-50"
                                                }
                                            >
                                                <td className="sticky left-0 px-3 py-2 text-sm font-medium whitespace-nowrap bg-inherit">
                                                    {row.name}
                                                </td>
                                                <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                    {formatEUR(
                                                        Number(
                                                            row.lastYear.replace(
                                                                ",",
                                                                "."
                                                            )
                                                        )
                                                    )}{" "}
                                                    €
                                                </td>
                                                <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                    {formatEUR(
                                                        Number(
                                                            row.currentYear.replace(
                                                                ",",
                                                                "."
                                                            )
                                                        )
                                                    )}{" "}
                                                    €
                                                </td>
                                                <td
                                                    className={`px-3 py-2 text-sm whitespace-nowrap ${
                                                        parseInt(row.percent) >
                                                        0
                                                            ? "text-green-600"
                                                            : parseInt(
                                                                  row.percent
                                                              ) === 0
                                                            ? ""
                                                            : "text-red-600"
                                                    }`}
                                                >
                                                    {row.percent}
                                                </td>
                                                <td className="px-3 py-2 text-sm text-blue-600 whitespace-nowrap">
                                                    {row.invoicesNum}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* ENOTE Chart */}
                            <div className="h-[500px] sm:h-[600px] w-full mt-8 sm:mt-10 sm:mx-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={agentsData.map((aData) => ({
                                            ...aData,
                                            currentYear: parseFloat(
                                                aData.currentYear
                                            ),
                                        }))}
                                        margin={{
                                            top: 20,
                                            right: 0,
                                            left: 0,
                                            bottom: 100,
                                        }}
                                        barSize={30}
                                        maxBarSize={40}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={100}
                                            interval={0}
                                            tick={{ fontSize: 12 }}
                                            tickMargin={5}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <YAxis
                                            dataKey="currentYear"
                                            tick={{ fontSize: 12 }}
                                            width={isLower ? 5 : 85}
                                            tickMargin={2}
                                            domain={[0, "auto"]}
                                            tickFormatter={(value) => {
                                                return isLower
                                                    ? ""
                                                    : "€ " + value;
                                            }}
                                        />
                                        <Tooltip
                                            formatter={(value) => {
                                                return `${formatNumber(
                                                    value
                                                )} €`;
                                            }}
                                            contentStyle={{
                                                fontSize: "1rem",
                                                padding: "0.75rem",
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.95)",
                                                border: "1px solid #e5e7eb",
                                            }}
                                            labelStyle={{
                                                fontWeight: "bold",
                                                marginBottom: "0.5rem",
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: "0.875rem",
                                                paddingTop: "1rem",
                                            }}
                                            verticalAlign="bottom"
                                            height={50}
                                            iconSize={12}
                                            iconType="circle"
                                        />
                                        <Bar
                                            dataKey="currentYear"
                                            fill="#EF4444"
                                            name={`Leto ${selectedYear}`}
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
